import {ParsedToken} from "../../auth/types";
import {useEffect, useState} from "react";
import {useChatToken} from "./use-chat-token";
import {ChatMessage} from "../types/chat-message";
import {ChatConversation} from "../types/chat-conversation";
import {RocketChatService} from "./client/RocketChatService";
import {RocketChatHttpClient} from "./client/RocketChatHttpClient";
import {RocketChatSocketClient} from "./client/RocketChatSocketClient";

interface RocketChatHook {
  conversations: ChatConversation[],
  messages: ChatMessage[],
  refreshRooms: () => void,
  formatMediaUrl: (path: string) => string,
  sendAttachment: (conversationId: string, file: File) => Promise<void>,
  sendChatMessage: (conversationId: string, message: string) => void,
  loadMoreHistory: (conversationId: string, limit: number) => Promise<void>,
  hasUnloadedHistory: (conversationId: string) => boolean,
  deleteMessage: (conversationId: string, messageId: string) => void,
  connected: boolean,
  loaded: boolean
}

const rocketChatService = new RocketChatService(new RocketChatSocketClient(), new RocketChatHttpClient())

export const useRocketChat = (rocketChatWebsocketUrl: string, rocketChatAuthUrl: string, user?: ParsedToken, accessToken?: string, onNewMessage?: (message: ChatMessage) => void): RocketChatHook  => {
  const {chatToken, userId} = useChatToken(rocketChatAuthUrl, user, accessToken)
  const [connected, setConnected] = useState(false)

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [rooms, setRooms] = useState<ChatConversation[]>([])

  useEffect(() => {
    let hasConnected = false;
    if (chatToken) {
      rocketChatService.connect(chatToken).then(() => {
        setConnected(true)
      })
      rocketChatService.onHistoryChanged(history => setMessages([...history]))
      rocketChatService.onRoomsChanged(rooms => setRooms([...rooms]))
      hasConnected = true
    }
    return () => {
      if (hasConnected) {
        rocketChatService.disconnect()
      }
    }
  }, [chatToken]);

  return {
    sendChatMessage: (conversationId: string, message: string) => rocketChatService.sendChatMessage(conversationId, message),
    conversations: rooms || [],
    messages,
    refreshRooms: () => rocketChatService.updateRooms(),
    loadMoreHistory: async (conversationId: string, limit: number) => rocketChatService.loadMoreHistory(conversationId, limit), // loadMoreHistory,
    connected,
    loaded: connected,
    hasUnloadedHistory: (conversationId: string) => rocketChatService.canLoadMoreHistory(conversationId),
    formatMediaUrl: (path) => rocketChatService.formatMediaUrl(userId!, chatToken!, path),
    deleteMessage: (conversationId, messageId) => rocketChatService.deleteChatMessage(userId!, chatToken!, conversationId, messageId),
    sendAttachment: (conversationId, attachment) => rocketChatService.sendAttachment(userId!, chatToken!, conversationId, attachment),
  }
}
